import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Volume2,
  Loader2,
  CheckCircle,
  XCircle,
  ChevronRight,
  Unlock,
  Star,
  Heart
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

import VocabProgressStats from './VocabProgressStats';
import { MasteryLevelUpNotification, CategoryCompletionNotification } from './MasteryLevelUpNotification';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Default mastery levels (fallback if config not loaded)
const DEFAULT_MASTERY_CONFIG = {
  mastery_levels: [
    { level: 'shokyu', threshold: 2, bonus_points: 5 },
    { level: 'chukyu', threshold: 4, bonus_points: 10 },
    { level: 'jokyu', threshold: 8, bonus_points: 25 },
    { level: 'tatsujin', threshold: 16, bonus_points: 50 },
  ],
  mastered_threshold: 8
};

// Japanese mastery level display names and colors
const MASTERY_LEVEL_DISPLAY = {
  unlearned: { label: 'New', color: 'bg-gray-300 dark:bg-gray-600', textColor: 'text-gray-600 dark:text-gray-300' },
  shokyu: { label: 'Shokyu', japanese: '初級', color: 'bg-green-400 dark:bg-green-600', textColor: 'text-green-700 dark:text-green-300' },
  chukyu: { label: 'Chukyu', japanese: '中級', color: 'bg-blue-400 dark:bg-blue-600', textColor: 'text-blue-700 dark:text-blue-300' },
  jokyu: { label: 'Jokyu', japanese: '上級', color: 'bg-purple-500 dark:bg-purple-600', textColor: 'text-purple-700 dark:text-purple-300' },
  tatsujin: { label: 'Tatsujin', japanese: '達人', color: 'bg-yellow-500 dark:bg-yellow-600', textColor: 'text-yellow-700 dark:text-yellow-300' },
};

// Vocabulary categories for display
const VOCAB_CATEGORIES = {
  general: 'General',
  greetings: 'Greetings',
  commands: 'Commands',
  counting: 'Counting',
  techniques: 'Techniques',
  body_parts: 'Body Parts',
  positions: 'Positions/Stances',
  etiquette: 'Etiquette',
  weapons: 'Weapons',
  directions: 'Directions',
  equipment: 'Equipment',
};

/**
 * Quiz-based vocabulary practice with multiple choice questions.
 * Shows romaji -> English or English -> romaji questions with 4 options.
 */
const VocabQuizMode = ({
  studentId,
  token,
  showKanji = false,
  category = '',
  onCategoryChange
}) => {
  const [terms, setTerms] = useState([]);
  const [allTerms, setAllTerms] = useState([]); // All terms for counting
  const [currentIndex, setCurrentIndex] = useState(0);
  const [question, setQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({}); // {category: count}
  const [questionType, setQuestionType] = useState('romaji_to_english');

  // Feedback state
  const [feedback, setFeedback] = useState(null);
  const [levelUpMessage, setLevelUpMessage] = useState(null);

  // Mastery level-up notification state
  const [levelUpEvent, setLevelUpEvent] = useState(null);
  const [categoryCompletion, setCategoryCompletion] = useState(null);

  // Response timing
  const [questionStartTime, setQuestionStartTime] = useState(null);

  // Favorites tracking
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [togglingFavorite, setTogglingFavorite] = useState(false);

  // Transition state for smooth question changes
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Mastery config (fetched from backend)
  const [masteryConfig, setMasteryConfig] = useState(DEFAULT_MASTERY_CONFIG);

  const audioRef = useRef(null);

  // Helper to get mastery level from correct count using config thresholds
  const getMasteryLevelFromCount = useCallback((correctCount) => {
    if (!correctCount || correctCount === 0) return 'unlearned';

    // Find the highest level where correct count >= threshold
    let currentLevel = 'unlearned';
    for (const level of masteryConfig.mastery_levels) {
      if (correctCount >= level.threshold) {
        currentLevel = level.level;
      }
    }
    return currentLevel;
  }, [masteryConfig]);

  // Helper to get the display info for a mastery level
  const getMasteryDisplay = useCallback((levelName) => {
    return MASTERY_LEVEL_DISPLAY[levelName] || MASTERY_LEVEL_DISPLAY.unlearned;
  }, []);

  // Helper to check if a level is considered "mastered" (jokyu or higher)
  const isMasteredLevel = useCallback((levelName) => {
    const masteredLevels = ['jokyu', 'tatsujin'];
    return masteredLevels.includes(levelName);
  }, []);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  // Fetch all quiz vocabulary (for counts) and filtered terms
  const fetchTerms = useCallback(async () => {
    setLoading(true);
    try {
      // First fetch ALL terms (no category filter) for counts
      const allResponse = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/vocabulary/quiz`,
        { headers: getAuthHeaders() }
      );
      if (allResponse.ok) {
        const allData = await allResponse.json();
        setAllTerms(allData);

        // Compute category counts
        const counts = {};
        allData.forEach(term => {
          const cat = term.category || 'general';
          counts[cat] = (counts[cat] || 0) + 1;
        });
        setCategoryCounts(counts);

        // Now filter for selected category
        let filteredData = allData;
        if (category) {
          filteredData = allData.filter(t => t.category === category);
        }

        // Filter to non-mastered terms first, then shuffle
        const nonMastered = filteredData.filter(t => !t.is_mastered);
        const shuffled = [...nonMastered].sort(() => Math.random() - 0.5);
        // If all mastered, include them but prioritize non-mastered
        if (shuffled.length === 0 && filteredData.length > 0) {
          const allShuffled = [...filteredData].sort(() => Math.random() - 0.5);
          setTerms(allShuffled);
        } else {
          setTerms(shuffled.length > 0 ? shuffled : filteredData);
        }
        setCurrentIndex(0);
      }
    } catch (error) {
      console.error('Failed to fetch quiz vocabulary:', error);
    } finally {
      setLoading(false);
    }
  }, [studentId, category, getAuthHeaders]);

  // Fetch progress
  const fetchProgress = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/vocabulary/progress`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setProgress(data);
        // Update favorites set
        if (data.favorite_term_ids) {
          setFavoriteIds(new Set(data.favorite_term_ids));
        }
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  }, [studentId, getAuthHeaders]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/vocabulary/categories`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, [studentId, getAuthHeaders]);

  // Fetch mastery config
  const fetchMasteryConfig = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/vocabulary/mastery-config`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setMasteryConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch mastery config:', error);
      // Keep using default config
    }
  }, [getAuthHeaders]);

  // Fetch question for current term
  const fetchQuestion = useCallback(async (termId) => {
    setLoadingQuestion(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/vocabulary/quiz-question/${termId}?question_type=${questionType}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setQuestion(data);
        setQuestionStartTime(Date.now());
        setSelectedAnswer(null);
        setIsAnswered(false);
        setFeedback(null);

        // End transition to fade in the new question
        // Small delay to ensure state has updated before animation
        setTimeout(() => setIsTransitioning(false), 50);
      }
    } catch (error) {
      console.error('Failed to fetch question:', error);
      setIsTransitioning(false); // End transition on error too
    } finally {
      setLoadingQuestion(false);
    }
  }, [studentId, questionType, getAuthHeaders]);

  // Initial load
  useEffect(() => {
    fetchTerms();
    fetchProgress();
    fetchCategories();
    fetchMasteryConfig();
  }, [fetchTerms, fetchProgress, fetchCategories, fetchMasteryConfig]);

  // Fetch question when term changes
  useEffect(() => {
    if (terms.length > 0 && currentIndex < terms.length) {
      fetchQuestion(terms[currentIndex].id);
    }
  }, [terms, currentIndex, fetchQuestion]);

  const currentTerm = terms[currentIndex];

  const playAudio = (audioUrl) => {
    if (audioRef.current && audioUrl) {
      audioRef.current.src = audioUrl;
      audioRef.current.load();
      audioRef.current.play();
    }
  };

  const toggleFavorite = async (termId) => {
    if (togglingFavorite) return;
    setTogglingFavorite(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/vocabulary/${termId}/favorite`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );
      if (response.ok) {
        const data = await response.json();
        setFavoriteIds(prev => {
          const newSet = new Set(prev);
          if (data.is_favorite) {
            newSet.add(termId);
          } else {
            newSet.delete(termId);
          }
          return newSet;
        });
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    } finally {
      setTogglingFavorite(false);
    }
  };

  const handleSelectAnswer = async (option) => {
    if (isAnswered || submitting) return;

    setSelectedAnswer(option.id);
    setSubmitting(true);

    const responseTime = questionStartTime ? Date.now() - questionStartTime : 0;
    const wasCorrect = option.is_correct;

    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/vocabulary/${currentTerm.id}/attempt`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            term_id: currentTerm.id,
            was_correct: wasCorrect,
            response_time_ms: responseTime,
            quiz_mode: 'quiz',
            question_type: questionType,
            selected_answer_id: option.id
          })
        }
      );

      if (response.ok) {
        const result = await response.json();

        // Update progress from response
        setProgress(prev => ({
          ...prev,
          current_correct_streak: result.current_correct_streak,
          longest_correct_streak: result.longest_correct_streak,
          today_correct_count: result.today_correct_count,
          best_daily_correct_count: result.best_daily_correct_count,
          // Increment mastered count if this word was just mastered
          total_mastered: result.just_mastered
            ? (prev?.total_mastered || 0) + 1
            : (prev?.total_mastered || 0)
        }));

        // Show feedback
        setFeedback({
          correct: wasCorrect,
          points: result.points_awarded || result.points_earned,
          pointsBreakdown: result.points_breakdown,
          termCorrectCount: result.correct_count || result.term_correct_count,
          masteryLevel: result.mastery_level,
          justMastered: result.just_mastered
        });

        // New mastery level notification
        if (result.level_up) {
          setLevelUpEvent(result.level_up);
        }

        // Category completion notification
        if (result.category_completion) {
          setCategoryCompletion(result.category_completion);
        }

        // Legacy: level unlocked notification (difficulty level, not mastery level)
        if (result.level_unlocked && !result.category_completion) {
          setLevelUpMessage(`Level ${result.new_level} unlocked in ${VOCAB_CATEGORIES[category] || category}!`);
          setTimeout(() => setLevelUpMessage(null), 3000);
        }
      }
    } catch (error) {
      console.error('Failed to record attempt:', error);
    } finally {
      setSubmitting(false);
      setIsAnswered(true);
    }
  };

  const handleNext = () => {
    // Start transition animation (fade out current card)
    setIsTransitioning(true);

    // After fade-out animation, update index to trigger new question fetch
    setTimeout(() => {
      // Reset answer state but keep question data to maintain card size
      setSelectedAnswer(null);
      setIsAnswered(false);
      setFeedback(null);
      // Don't set question to null - keep card skeleton in place

      if (currentIndex < terms.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        // Completed all terms - refresh from server to get updated mastery status
        setCurrentIndex(0);
        fetchProgress();
        fetchTerms();
      }
      // Transition stays true until new question loads
    }, 200);
  };

  const handleCategorySelect = (newCategory) => {
    if (onCategoryChange) {
      onCategoryChange(newCategory);
    }
    // Reset state
    setCurrentIndex(0);
    setQuestion(null);
    setSelectedAnswer(null);
    setIsAnswered(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Build unique categories list from actual data
  const availableCategories = Object.keys(categoryCounts).sort();

  // Filter controls - always shown
  const renderFilters = () => (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={category}
          onChange={(e) => handleCategorySelect(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-background text-foreground"
        >
          <option value="">All Categories ({allTerms.length})</option>
          {availableCategories.map(cat => (
            <option key={cat} value={cat}>
              {VOCAB_CATEGORIES[cat] || cat} ({categoryCounts[cat] || 0})
              {progress?.category_levels?.[cat] > 1
                ? ` - Level ${progress.category_levels[cat]}`
                : ''}
            </option>
          ))}
        </select>

        <select
          value={questionType}
          onChange={(e) => setQuestionType(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-background text-foreground"
        >
          <option value="romaji_to_english">Japanese → English</option>
          <option value="english_to_romaji">English → Japanese</option>
        </select>
      </div>

      {progress && (
        <VocabProgressStats
          currentCorrectStreak={progress.current_correct_streak}
          longestCorrectStreak={progress.longest_correct_streak}
          todayCorrectCount={progress.today_correct_count}
          bestDailyCorrectCount={progress.best_daily_correct_count}
          totalMastered={progress.total_mastered}
          termMastery={progress.term_mastery}
          masteryConfig={masteryConfig}
          compact={true}
        />
      )}
    </div>
  );

  if (terms.length === 0) {
    return (
      <div className="space-y-4">
        {/* Hidden audio element */}
        <audio ref={audioRef} />

        {/* Filters - always shown */}
        {renderFilters()}

        <Card>
          <CardContent className="p-8 text-center">
            <Volume2 className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No quiz vocabulary available
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {category
                ? `No terms with audio found in "${VOCAB_CATEGORIES[category] || category}". Try a different category.`
                : 'No vocabulary terms with audio available for your belt level yet.'}
            </p>
          </CardContent>
        </Card>

        {/* Full Progress Stats */}
        {progress && (
          <VocabProgressStats
            currentCorrectStreak={progress.current_correct_streak}
            longestCorrectStreak={progress.longest_correct_streak}
            todayCorrectCount={progress.today_correct_count}
            bestDailyCorrectCount={progress.best_daily_correct_count}
            totalMastered={progress.total_mastered}
            totalAttempted={progress.total_attempted}
            categoryLevels={progress.category_levels}
            termMastery={progress.term_mastery}
            masteryConfig={masteryConfig}
            compact={false}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Hidden audio element */}
      <audio ref={audioRef} />

      {/* Level Up Toast (legacy difficulty level unlock) */}
      {levelUpMessage && (
        <div className="fixed top-4 right-4 z-50 animate-bounce">
          <div className="bg-purple-600 text-white px-4 py-3 rounded-lg shadow-lg font-bold flex items-center gap-2">
            <Unlock className="h-5 w-5" />
            {levelUpMessage}
          </div>
        </div>
      )}

      {/* Mastery Level Up Notification */}
      {levelUpEvent && (
        <MasteryLevelUpNotification
          level={levelUpEvent.new_level}
          termJapanese={levelUpEvent.term_japanese}
          termRomaji={levelUpEvent.term_romaji}
          termEnglish={levelUpEvent.term_english}
          bonusPoints={levelUpEvent.bonus_points}
          onComplete={() => setLevelUpEvent(null)}
        />
      )}

      {/* Category Completion Notification */}
      {categoryCompletion && (
        <CategoryCompletionNotification
          level={categoryCompletion.level}
          category={categoryCompletion.category}
          difficulty={categoryCompletion.difficulty}
          bonusPoints={categoryCompletion.bonus_points}
          newDifficultyUnlocked={categoryCompletion.new_difficulty_unlocked}
          onComplete={() => setCategoryCompletion(null)}
        />
      )}

      {/* Header with filters and stats */}
      {renderFilters()}

      {/* Progress bar */}
      <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div
          className="bg-purple-600 rounded-full h-2 transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / terms.length) * 100}%` }}
        />
      </div>
      <p className="text-center text-sm text-gray-500 dark:text-gray-400">
        Question {currentIndex + 1} of {terms.length}
        {currentTerm?.correct_count > 0 && (() => {
          const levelName = getMasteryLevelFromCount(currentTerm.correct_count);
          const levelDisplay = getMasteryDisplay(levelName);
          const isMastered = isMasteredLevel(levelName);
          const nextLevel = masteryConfig.mastery_levels.find(l => l.threshold > currentTerm.correct_count);

          return (
            <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              levelName === 'tatsujin'
                ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300'
                : levelName === 'jokyu'
                ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                : levelName === 'chukyu'
                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                : levelName === 'shokyu'
                ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }`}>
              <Star className={`h-3 w-3 ${isMastered ? 'fill-current' : ''}`} />
              {levelDisplay.label}
              {levelDisplay.japanese && <span className="opacity-70">({levelDisplay.japanese})</span>}
              {nextLevel && (
                <span className="opacity-60">({currentTerm.correct_count}/{nextLevel.threshold})</span>
              )}
            </span>
          );
        })()}
      </p>

      {/* Question Card */}
      {loadingQuestion && !question ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </div>
      ) : question ? (
        <Card className={`overflow-hidden transition-all duration-200 ${isTransitioning ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}>
          <CardContent className="p-0">
            {/* Question header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6">
              <p className="text-sm text-purple-200 mb-2">{question.question_text}</p>

              {questionType === 'romaji_to_english' ? (
                <div className="text-center">
                  {showKanji && question.japanese && (
                    <p className="text-4xl font-bold mb-2">{question.japanese}</p>
                  )}
                  <p className="text-3xl font-bold">{question.display_text}</p>
                  {question.audio_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => playAudio(question.audio_url)}
                      className="mt-3 text-white hover:bg-purple-500"
                    >
                      <Volume2 className="h-5 w-5 mr-2" />
                      Play Audio
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-2xl font-medium text-center">{question.display_text}</p>
              )}
            </div>

            {/* Answer options */}
            <div className="p-4 space-y-3">
              {question.options.map((option) => {
                let optionClass = 'w-full p-4 text-left rounded-lg border-2 transition-all ';

                if (isAnswered) {
                  if (option.is_correct) {
                    optionClass += 'border-green-500 bg-green-50 dark:bg-green-900/30 ';
                  } else if (selectedAnswer === option.id && !option.is_correct) {
                    optionClass += 'border-red-500 bg-red-50 dark:bg-red-900/30 ';
                  } else {
                    optionClass += 'border-gray-200 dark:border-gray-700 opacity-50 ';
                  }
                } else {
                  optionClass += selectedAnswer === option.id
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 '
                    : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 ';
                }

                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => !isAnswered && !submitting && handleSelectAnswer(option)}
                    className={`${optionClass} ${!isAnswered && !submitting ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {questionType === 'english_to_romaji' && (
                          <>
                            <span className="font-medium">{option.text}</span>
                            {showKanji && option.japanese && (
                              <span className="text-gray-500 dark:text-gray-400">{option.japanese}</span>
                            )}
                            {option.audio_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  playAudio(option.audio_url);
                                }}
                                className="h-8 w-8 p-0"
                              >
                                <Volume2 className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}
                        {questionType === 'romaji_to_english' && (
                          <span className="font-medium">{option.text}</span>
                        )}
                      </div>

                      {isAnswered && option.is_correct && (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      )}
                      {isAnswered && selectedAnswer === option.id && !option.is_correct && (
                        <XCircle className="h-5 w-5 text-red-600" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Feedback section */}
            {isAnswered && feedback && (
              <div className={`p-4 border-t ${feedback.correct ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30' : 'bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/30 dark:to-orange-900/30'}`}>
                {/* Top row: Result and Mastery Level */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {feedback.correct ? (
                      <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-full">
                        <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                      </div>
                    ) : (
                      <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-full">
                        <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                      </div>
                    )}
                    <div>
                      <p className={`font-bold text-lg ${feedback.correct ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                        {feedback.correct ? 'Correct!' : 'Not quite...'}
                      </p>
                      {feedback.justMastered && (
                        <p className="text-yellow-600 font-medium text-sm flex items-center gap-1">
                          <Star className="h-4 w-4 fill-yellow-500" /> Word Mastered!
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Favorite button */}
                  {(currentTerm?.is_mastered || feedback.justMastered) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleFavorite(currentTerm.id)}
                      disabled={togglingFavorite}
                      className={`${
                        favoriteIds.has(currentTerm.id)
                          ? 'text-pink-500 hover:text-pink-600'
                          : 'text-gray-400 hover:text-pink-500'
                      }`}
                      title={favoriteIds.has(currentTerm.id) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <Heart
                        className={`h-5 w-5 ${favoriteIds.has(currentTerm.id) ? 'fill-current' : ''}`}
                      />
                    </Button>
                  )}
                </div>

                {/* Bottom row: Points breakdown and Mastery Progress */}
                <div className="flex items-end justify-between gap-4 pt-3 border-t border-gray-200/50">
                  {/* Points breakdown */}
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1 font-medium">Points Earned</p>
                    <div className="flex flex-wrap items-center gap-2">
                      {feedback.pointsBreakdown ? (
                        <>
                          {feedback.pointsBreakdown.base > 0 && (
                            <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded">
                              Base: +{feedback.pointsBreakdown.base}
                            </span>
                          )}
                          {feedback.pointsBreakdown.streak_bonus > 0 && (
                            <span className="text-xs bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded">
                              Streak: +{feedback.pointsBreakdown.streak_bonus}
                            </span>
                          )}
                          {feedback.pointsBreakdown.level_bonus > 0 && (
                            <span className="text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded">
                              Level Up: +{feedback.pointsBreakdown.level_bonus}
                            </span>
                          )}
                          {feedback.pointsBreakdown.category_bonus > 0 && (
                            <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                              Category: +{feedback.pointsBreakdown.category_bonus}
                            </span>
                          )}
                        </>
                      ) : null}
                      <span className={`text-sm font-bold ${feedback.correct ? 'text-green-600' : 'text-gray-500'}`}>
                        = {feedback.points} pts
                      </span>
                    </div>
                  </div>

                  {/* Mastery Level Progress */}
                  <div className="text-right">
                    {(() => {
                      const levelName = feedback.masteryLevel || 'unlearned';
                      const levelDisplay = getMasteryDisplay(levelName);
                      const isMastered = isMasteredLevel(levelName);
                      const levelOrder = ['shokyu', 'chukyu', 'jokyu', 'tatsujin'];
                      const currentLevelIndex = levelOrder.indexOf(levelName);

                      return (
                        <>
                          <p className="text-xs text-gray-500 mb-1 font-medium">
                            Word Mastery
                            {isMastered && (
                              <Star className="h-3 w-3 inline ml-1 text-yellow-500 fill-yellow-500" />
                            )}
                          </p>
                          <div className="flex items-center gap-0.5 justify-end">
                            {masteryConfig.mastery_levels.map((level, index) => {
                              const isReached = levelName !== 'unlearned' && index <= currentLevelIndex;
                              const levelColors = {
                                shokyu: 'bg-green-400',
                                chukyu: 'bg-blue-400',
                                jokyu: 'bg-purple-500',
                                tatsujin: 'bg-yellow-500'
                              };
                              return (
                                <div
                                  key={level.level}
                                  className={`h-2 w-5 rounded-sm transition-all ${
                                    isReached ? levelColors[level.level] : 'bg-gray-200 dark:bg-gray-700'
                                  }`}
                                  title={`${level.level.charAt(0).toUpperCase() + level.level.slice(1)} (${level.threshold}+)`}
                                />
                              );
                            })}
                          </div>
                          <p className={`text-xs mt-0.5 ${levelDisplay.textColor}`}>
                            {levelDisplay.label}
                            {levelDisplay.japanese && ` (${levelDisplay.japanese})`}
                          </p>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Next Button */}
      {isAnswered && !isTransitioning && (
        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={handleNext}
            disabled={isTransitioning}
            variant="memberPrimary"
            className="px-8"
          >
            Next Question
            <ChevronRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      )}

      {/* Full Progress Stats */}
      {progress && (
        <VocabProgressStats
          currentCorrectStreak={progress.current_correct_streak}
          longestCorrectStreak={progress.longest_correct_streak}
          todayCorrectCount={progress.today_correct_count}
          bestDailyCorrectCount={progress.best_daily_correct_count}
          totalMastered={progress.total_mastered}
          totalAttempted={progress.total_attempted}
          categoryLevels={progress.category_levels}
          termMastery={progress.term_mastery}
          masteryConfig={masteryConfig}
          compact={false}
        />
      )}
    </div>
  );
};

export default VocabQuizMode;
